import { request } from "../client";

import type {
  StudentActivity,
  StudentAssignment,
  StudentReport,
  StudentReportOptions,
  StudentsResponse,
  TeacherInvitationPreview,
  TeacherJoinLink,
  TeacherJoinOptions,
} from "@lingoshade/domain";

export type StudentsEndpoints = {
  assignments: (
    id: string,
    token?: string | null,
  ) => Promise<{ assignments: StudentAssignment[] }>;
  list: (
    role: "teacher" | "student",
    token?: string | null,
    timeZone?: string,
  ) => Promise<StudentsResponse>;
  invite: (email: string, token?: string | null) => Promise<void>;
  joinLink: (token?: string | null) => Promise<TeacherJoinLink>;
  preview: (
    value: string,
    token?: string | null,
  ) => Promise<TeacherInvitationPreview>;
  join: (
    value: string,
    token?: string | null,
    options?: TeacherJoinOptions,
  ) => Promise<void>;
  accept: (
    id: string,
    role: "teacher" | "student",
    token?: string | null,
  ) => Promise<void>;
  update: (
    id: string,
    action: "remove" | "reject" | "request-full" | "allow-full" | "deny-full",
    token?: string | null,
    invitationToken?: string,
  ) => Promise<void>;
  report: (
    id: string,
    options?: StudentReportOptions,
    token?: string | null,
  ) => Promise<StudentReport>;
  activity: (
    id: string,
    token?: string | null,
    offset?: number,
  ) => Promise<StudentActivity>;
};
export function createStudentsEndpoints(baseUrl: string): StudentsEndpoints {
  return {
    assignments: (id, token): Promise<{ assignments: StudentAssignment[] }> =>
      request(baseUrl, `/students/${id}/assignments`, { method: "GET", token }),
    list: (role, token, timeZone): Promise<StudentsResponse> =>
      request(baseUrl, "/students", {
        method: "GET",
        query: { role, timeZone },
        token,
      }),
    invite: (email, token): Promise<void> =>
      request(baseUrl, "/students/invite", {
        method: "POST",
        body: { email },
        token,
      }),
    joinLink: (token): Promise<TeacherJoinLink> =>
      request(baseUrl, "/students/join-link", { method: "POST", token }),
    preview: (value, token): Promise<TeacherInvitationPreview> =>
      request(baseUrl, `/students/invitations/${encodeURIComponent(value)}`, {
        method: "GET",
        token,
      }),
    join: (value, token, options): Promise<void> =>
      request(baseUrl, "/students/join", {
        method: "POST",
        body: {
          value,
          consent: true,
          ...(options?.fullActivityConsent
            ? { fullActivityConsent: true }
            : {}),
        },
        token,
      }),
    accept: (id, role, token): Promise<void> =>
      request(baseUrl, `/students/${id}/accept`, {
        method: "POST",
        body: { role, consent: true },
        token,
      }),
    update: (id, action, token, invitationToken): Promise<void> =>
      request(baseUrl, `/students/${id}`, {
        method: "PATCH",
        body: { action, ...(invitationToken ? { invitationToken } : {}) },
        token,
      }),
    report: (id, options, token): Promise<StudentReport> =>
      request(baseUrl, `/students/${id}/report`, {
        method: "GET",
        token,
        query: {
          days: options?.days,
          section: options?.section,
          textId: options?.textId,
          offset: options?.offset,
        },
      }),
    activity: (id, token, offset): Promise<StudentActivity> =>
      request(baseUrl, `/students/${id}/activity`, {
        method: "GET",
        token,
        query: { offset },
      }),
  };
}
